import {
  Children,
  cloneElement,
  useId,
  useMemo,
  useState,
  type FocusEvent as ReactFocusEvent,
  type HTMLAttributes,
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  type TouchEvent as ReactTouchEvent,
  type TouchEventHandler,
} from 'react';
import Popper, {
  type PopperPlacementType,
  type PopperProps,
} from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useTheme } from '../../theme';
import { useIsTouchDevice } from '../../hooks';
import { useTooltipStyles } from './tooltip.styles';
import { TooltipPlain } from './plain';
import { useTooltip, type TooltipChangeEvent } from './useTooltip';
import { TooltipArrow } from './tooltipArrow';
import { chainHandlers, getElementRef, mergeRefs } from './tooltip.utils';

export type TooltipComponentsProps = {
  popper?: {
    sx?: PopperProps['sx'];
  };
  /** Forwarded to the popup element (the floating tooltip body). */
  tooltip?: {
    ref?: Ref<HTMLDivElement>;
  } & HTMLAttributes<HTMLDivElement>;
};

export type TooltipProps = {
  /** The single anchor element the tooltip describes. */
  children: ReactElement;
  /** String titles are wrapped in `TooltipPlain`; any other node is rendered as-is. */
  title?: ReactNode;
  placement?: PopperPlacementType;
  /** Displacement (px) from the anchor along the placement axis. */
  offset?: string | number;
  /** Placements Popper may flip to when the preferred one doesn't fit. */
  popoverFallbackPlacements?: PopperPlacementType[];
  /** Renders the anchor without any tooltip behaviour. */
  isDisabled?: boolean;
  /** Dims the tooltip (e.g. while a call control bar is fading out). */
  isFaded?: boolean;
  /** When provided, the tooltip is controlled and its visibility follows this value. */
  open?: boolean;
  onOpen?: (event: TooltipChangeEvent) => void;
  onClose?: (event: TooltipChangeEvent) => void;
  disableHoverListener?: boolean;
  disableFocusListener?: boolean;
  disableTouchListener?: boolean;
  /** When true, the tooltip closes even if the pointer moves onto the popup. */
  disableInteractive?: boolean;
  arrow?: boolean;
  /** Hover open delay (ms). Touch opens immediately on tap and ignores this. */
  enterDelay?: number;
  /** Hover close delay (ms). Touch closes immediately and ignores this. */
  leaveDelay?: number;
  id?: string;
  className?: string;
  /** Forwarded to the anchor element (alongside any handler the child already has). */
  onTouchStart?: TouchEventHandler;
  onTouchEnd?: TouchEventHandler;
  componentsProps?: TooltipComponentsProps;
};

/** Event handlers the cloned anchor may already carry, which we must preserve. */
type AnchorChildProps = {
  ref?: Ref<HTMLElement>;
  'aria-describedby'?: string;
  onMouseEnter?: (event: ReactMouseEvent) => void;
  onMouseLeave?: (event: ReactMouseEvent) => void;
  onFocus?: (event: ReactFocusEvent) => void;
  onBlur?: (event: ReactFocusEvent) => void;
  onClick?: (event: ReactMouseEvent) => void;
  onTouchStart?: (event: ReactTouchEvent) => void;
  onTouchEnd?: (event: ReactTouchEvent) => void;
};

/** Arrow length (px) from the tooltip body to its tip. */
const ARROW_SIZE = 7;

/**
 * Baseline distance (px) from the anchor to the tooltip body, before the caller's
 * `offset` is added. Matches the gap the previous `@mui/material/Tooltip` used
 * (its `marginBottom: 14px`), so existing per-call `offset` values land identically.
 */
const ARROW_GAP = 14;

const DEFAULT_FALLBACK_PLACEMENTS: PopperPlacementType[] = [
  'top',
  'bottom',
  'right',
  'left',
];

/**
 * Tooltip built on Popper + ClickAwayListener (the same primitives `popover` and
 * `infoIcon` use). It owns its open/close state via {@link useTooltip}, which makes it
 * close reliably on touch — unlike the previous `@mui/material/Tooltip` wrapper, which
 * stayed open after a tap because emulated mouse events kept re-opening it.
 *
 * The public prop surface mirrors the props the ~30 consumers actually used, including
 * the controlled (`open`/`onOpen`/`onClose`), `componentsProps`, and `disable*Listener`
 * paths relied on by `infoIcon`, `menuCell`, and `callControlBarCell`.
 */
export const Tooltip = ({
  children,
  title,
  placement = 'top',
  offset,
  popoverFallbackPlacements = DEFAULT_FALLBACK_PLACEMENTS,
  isDisabled = false,
  isFaded = false,
  open,
  onOpen,
  onClose,
  disableHoverListener = false,
  disableFocusListener = false,
  disableTouchListener = false,
  disableInteractive = false,
  arrow = true,
  enterDelay = 200,
  leaveDelay = 50,
  id,
  className,
  onTouchStart,
  onTouchEnd,
  componentsProps,
}: TooltipProps): JSX.Element => {
  const theme = useTheme('dark');
  const styles = useTooltipStyles('dark');
  const isTouchDevice = useIsTouchDevice();
  const generatedId = useId();
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);

  // Stable element reference so the popup body subtree (TooltipPlain → Caption →
  // Box) isn't re-rendered on every Grow transition phase / popper position update.
  const content = useMemo(
    () =>
      typeof title === 'string' && Boolean(title) ? (
        <TooltipPlain text={title} />
      ) : (
        title
      ),
    [title],
  );
  const hasContent = !isDisabled && Boolean(content);

  const tooltip = useTooltip({
    open,
    onOpen,
    onClose,
    enterDelay,
    leaveDelay,
    disableHoverListener,
    disableFocusListener,
    disableTouchListener,
    disableInteractive,
    isTouchDevice,
  });

  const child = Children.only(children) as ReactElement<AnchorChildProps>;
  const childProps = child.props as AnchorChildProps;
  const childRef = getElementRef(child);
  const { setAnchorRef } = tooltip;

  const mergedRef = useMemo(
    () => mergeRefs(childRef, setAnchorRef),
    [childRef, setAnchorRef],
  );

  const modifiers = useMemo<PopperProps['modifiers']>(() => {
    const base =
      typeof offset === 'string' ? Number.parseInt(offset, 10) : (offset ?? 0);
    const distance = base + (arrow ? ARROW_GAP : 0);
    return [
      { name: 'offset', options: { offset: [0, distance] } },
      {
        name: 'flip',
        options: { fallbackPlacements: popoverFallbackPlacements },
      },
      {
        name: 'preventOverflow',
        options: { altAxis: true, tether: false, boundary: 'clippingParents' },
      },
      {
        name: 'arrow',
        enabled: arrow,
        options: { element: arrowEl, padding: 4 },
      },
    ];
  }, [offset, arrow, popoverFallbackPlacements, arrowEl]);

  // Stable element reference (setArrowEl is a stable state setter), so the arrow
  // isn't re-rendered on every Grow transition phase.
  const arrowElement = useMemo(
    () =>
      arrow ? (
        <TooltipArrow
          ref={setArrowEl}
          size={ARROW_SIZE}
          color={theme.tooltip.background}
        />
      ) : null,
    [arrow, theme.tooltip.background],
  );

  const popperSx = useMemo<PopperProps['sx']>(
    () =>
      [
        { zIndex: theme.zIndex.tooltip },
        ...(isFaded ? [styles.faded] : []),
        ...(componentsProps?.popper?.sx
          ? Array.isArray(componentsProps.popper.sx)
            ? componentsProps.popper.sx
            : [componentsProps.popper.sx]
          : []),
      ] as PopperProps['sx'],
    [theme.zIndex.tooltip, isFaded, styles.faded, componentsProps?.popper?.sx],
  );

  // No content to show — render the anchor untouched, but still forward the
  // touch handlers (matches the old behaviour of nulling the title).
  if (!hasContent) {
    return (
      <>
        {cloneElement(child, {
          onTouchStart: chainHandlers(childProps.onTouchStart, onTouchStart),
          onTouchEnd: chainHandlers(childProps.onTouchEnd, onTouchEnd),
        })}
      </>
    );
  }

  const effectiveTooltipId = componentsProps?.tooltip?.id ?? id ?? generatedId;
  // When a consumer sets its own tooltip id it manages aria itself (e.g. infoIcon).
  const consumerManagesAria = componentsProps?.tooltip?.id !== undefined;
  const anchorAriaDescribedBy =
    !consumerManagesAria && tooltip.isOpen
      ? [childProps['aria-describedby'], effectiveTooltipId]
          .filter(Boolean)
          .join(' ')
      : childProps['aria-describedby'];

  const anchor = cloneElement(child, {
    ref: mergedRef,
    'aria-describedby': anchorAriaDescribedBy,
    onMouseEnter: chainHandlers(
      childProps.onMouseEnter,
      tooltip.anchorListeners.onMouseEnter,
    ),
    onMouseLeave: chainHandlers(
      childProps.onMouseLeave,
      tooltip.anchorListeners.onMouseLeave,
    ),
    onFocus: chainHandlers(childProps.onFocus, tooltip.anchorListeners.onFocus),
    onBlur: chainHandlers(childProps.onBlur, tooltip.anchorListeners.onBlur),
    // Touch tap-toggle (the anchorListener) is merged with the child's own onClick.
    onClick: chainHandlers(childProps.onClick, tooltip.anchorListeners.onClick),
    // Tooltip-level touch handlers are forwarded to the anchor (e.g. callControlBarCell).
    onTouchStart: chainHandlers(childProps.onTouchStart, onTouchStart),
    onTouchEnd: chainHandlers(childProps.onTouchEnd, onTouchEnd),
  });

  const {
    ref: popupRef,
    role: popupRole,
    onMouseEnter: popupOnMouseEnter,
    onMouseLeave: popupOnMouseLeave,
    ...popupRest
  } = componentsProps?.tooltip ?? {};

  return (
    <>
      {anchor}
      {tooltip.isOpen && tooltip.anchorEl && (
        <ClickAwayListener onClickAway={tooltip.handleClickAway}>
          <Popper
            open
            anchorEl={tooltip.anchorEl}
            placement={placement}
            modifiers={modifiers}
            // Neutralise MUI Popper's default `role="tooltip"` on the positioning
            // root; the role belongs on the body element below (which also carries
            // the id that `aria-describedby` targets), not the wrapper.
            role='presentation'
            className={className}
            sx={popperSx}
          >
            {/* Relative wrapper: positioning context for the absolutely-positioned arrow. */}
            <div css={{ position: 'relative' }}>
              {arrowElement}
              <div
                {...popupRest}
                ref={popupRef}
                id={effectiveTooltipId}
                role={popupRole ?? 'tooltip'}
                onMouseEnter={chainHandlers(
                  tooltip.popperListeners.onMouseEnter,
                  popupOnMouseEnter,
                )}
                onMouseLeave={chainHandlers(
                  tooltip.popperListeners.onMouseLeave,
                  popupOnMouseLeave,
                )}
                css={styles.tooltip}
              >
                {content}
              </div>
            </div>
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};
